/* ============================================================================
   Kawaii bag: the cart drawer and the cart page of the 2026 redesign.
   Markup: snippets/kc-cart-drawer.liquid (inside the theme's <mini-cart>),
   sections/kc-cart.liquid and sections/kc-cart-recs.liquid. Styles:
   kawaii-cart.css.

   <kc-bag> (drawer and page)
     Steppers, typed quantities and remove buttons post to /cart/change.js
     by line key with bundled section rendering, then swap in the fresh
     HTML: the drawer's "mini-cart" section or the cart page's own section,
     plus the header's cart bubble. Taps on a stepper are debounced per
     line, so three quick taps on + make one request.
     A swap replaces this element, so whatever has to outlive it (focus,
     the drawer's scroll position, a line's error, an unsaved note, fetched
     suggestions) is kept at module level and picked up by the next one.
     The order note saves as it's typed (/cart/update.js).
     Drawer: fills its few suggestions from kc-cart-recs.
     Cart page: catches the kit's <add-to-cart> buttons anywhere on the page
     and adds in place; the theme's element would post the whole page.

   <kc-bag-recs> (cart page)
     "You may also like": filled from kc-cart-recs, refilled whenever the
     bag changes, hidden when there's nothing to show.

   While the redesign is a preview (?view=kawaii), the drawer that comes
   back with a cart change is the old one; kawaii-kit.js (part 3) swaps it
   for the new one before this sees it. The cart page's own section is found
   by its id, so it needs no help.
   ============================================================================ */
(() => {
  if (customElements.get('kc-bag')) return;

  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.cartStrings) || {};
  const CHANGE_URL = `${routes.cart_change_url || '/cart/change'}.js`;
  const UPDATE_URL = `${routes.cart_update_url || '/cart/update'}.js`;
  const ADD_URL = `${routes.cart_add_url || '/cart/add'}.js`;
  const ERROR = strings.error || 'There was an error while updating your cart. Please try again.';
  const STEP_DELAY = 380;
  const NOTE_DELAY = 500;

  const keep = {
    focus: null, // { key, index, ctl } of the control to refocus after a swap
    scroll: null, // the drawer's scrollTop before a swap
    bar: null, // the free-shipping bar's width before a swap
    error: null, // { key, text } to show on a line after a swap
    note: null, // a note typed but not saved yet
    recs: new Map() // suggestions HTML by URL
  };

  const here = () => window.location.pathname + window.location.search;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function sectionHTML(html) {
    if (typeof html !== 'string') return null;
    const el = parse(html).querySelector('.shopify-section');
    return el ? el.innerHTML : null;
  }

  async function post(url, data) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    });
    let json = {};
    try {
      json = await res.json();
    } catch (e) {
      /* an empty or HTML answer: treated as an error below */
    }
    // A cart that comes back with `errors` still changed (Shopify lowers a
    // quantity to what it can sell); the caller shows the message on it.
    if (!res.ok || json.status || (json.errors && !json.items)) {
      const message =
        json.description ||
        (typeof json.errors === 'string' ? json.errors : '') ||
        json.message ||
        ERROR;
      const err = new Error(message);
      err.cart = true;
      throw err;
    }
    return json;
  }

  /* One polite live region for the whole page: the bag's own markup is
     replaced on every change, which would swallow announcements. */
  let live;
  function announce(text) {
    if (!text) return;
    if (!live) {
      live = document.createElement('p');
      live.className = 'kc-vh';
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(() => {
      live.textContent = text;
    }, 60);
  }

  /* The order note, saved a moment after typing stops. */
  let noteTimer;
  function queueNote(value) {
    keep.note = value;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(saveNote, NOTE_DELAY);
  }

  function saveNote() {
    clearTimeout(noteTimer);
    if (keep.note === null) return;
    const value = keep.note;
    post(UPDATE_URL, { note: value })
      .then(() => {
        if (keep.note === value) keep.note = null;
      })
      .catch(() => {});
  }

  function ctlOf(el) {
    const picks = ['[data-kc-step="1"]', '[data-kc-step="-1"]', '[data-kc-qty]', '[data-kc-remove]', '[data-kc-note]', '[data-kc-note-toggle]'];
    return picks.find((sel) => el.matches(sel)) || null;
  }

  function lineTitle(line) {
    const a = line && line.querySelector('.kc-line__title');
    return a ? a.textContent.trim() : '';
  }

  class KcBag extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode === 'page' ? 'page' : 'drawer';
      this.timers = new Map();
      this.handlers = {
        click: (e) => this.onClick(e),
        change: (e) => this.onChange(e),
        input: (e) => this.onInput(e),
        keydown: (e) => this.onKeydown(e)
      };
      Object.keys(this.handlers).forEach((type) => this.addEventListener(type, this.handlers[type]));
      // Suggestions first: once fetched they fill in at once, and the drawer
      // needs its full height before its scroll position comes back.
      if (this.mode === 'drawer') this.fillDrawerRecs();
      this.restore();
    }

    disconnectedCallback() {
      Object.keys(this.handlers).forEach((type) => this.removeEventListener(type, this.handlers[type]));
      // Steps still waiting on their debounce go ahead: change() and render()
      // don't need this element to be on the page.
    }

    get lines() {
      return Array.from(this.querySelectorAll('[data-kc-line]'));
    }

    lineByKey(key) {
      return this.lines.find((line) => line.dataset.kcLine === key) || null;
    }

    sections() {
      return [this.mode === 'drawer' ? 'mini-cart' : this.dataset.section, 'cart-icon-bubble'];
    }

    /* --- events ----------------------------------------------------------- */

    onClick(event) {
      const target = event.target;
      if (!target.closest) return;
      const step = target.closest('[data-kc-step]');
      if (step) {
        event.preventDefault();
        this.step(step);
        return;
      }
      const remove = target.closest('[data-kc-remove]');
      if (remove) {
        event.preventDefault();
        this.remove(remove.closest('[data-kc-line]'));
        return;
      }
      const toggle = target.closest('[data-kc-note-toggle]');
      if (toggle) this.toggleNote(toggle);
    }

    onChange(event) {
      const input = event.target.closest && event.target.closest('[data-kc-qty]');
      if (input) this.typed(input);
    }

    onInput(event) {
      if (event.target.matches && event.target.matches('[data-kc-note]')) queueNote(event.target.value);
    }

    onKeydown(event) {
      // Enter in a quantity applies it here instead of submitting the form.
      if (event.key === 'Enter' && event.target.matches && event.target.matches('[data-kc-qty]')) {
        event.preventDefault();
        this.typed(event.target);
      }
    }

    /* --- quantities ------------------------------------------------------- */

    limits(input) {
      return {
        min: Math.max(1, Number(input.dataset.min) || 1),
        max: input.max ? Number(input.max) : Infinity,
        step: Number(input.step) || 1
      };
    }

    syncSteps(line) {
      const input = line.querySelector('[data-kc-qty]');
      const { min, max } = this.limits(input);
      const qty = Number(input.value) || 0;
      const minus = line.querySelector('[data-kc-step="-1"]');
      const plus = line.querySelector('[data-kc-step="1"]');
      if (minus) minus.setAttribute('aria-disabled', String(qty <= min));
      if (plus) plus.setAttribute('aria-disabled', String(qty >= max));
    }

    step(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      const line = button.closest('[data-kc-line]');
      const input = line && line.querySelector('[data-kc-qty]');
      if (!input || line.classList.contains('is-leaving')) return;
      const { min, max, step } = this.limits(input);
      const qty = Number(input.value) || 0;
      const next = Math.max(min, Math.min(max, qty + Number(button.dataset.kcStep) * step));
      if (next === qty) return;
      input.value = next;
      this.syncSteps(line);
      this.clearError(line);
      line.classList.add('is-pending');
      this.classList.add('is-busy');
      const key = line.dataset.kcLine;
      clearTimeout(this.timers.get(key));
      this.timers.set(
        key,
        setTimeout(() => {
          this.timers.delete(key);
          this.change(key, next, line);
        }, STEP_DELAY)
      );
    }

    typed(input) {
      const line = input.closest('[data-kc-line]');
      if (!line) return;
      const { min, max } = this.limits(input);
      let qty = Math.round(Number(input.value));
      if (!Number.isFinite(qty) || qty <= 0) qty = 0;
      else qty = Math.max(min, Math.min(max, qty));
      input.value = qty;
      this.syncSteps(line);
      this.clearError(line);
      const key = line.dataset.kcLine;
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
      if (qty === 0) line.classList.add('is-leaving');
      this.change(key, qty, line);
    }

    remove(line) {
      if (!line || line.classList.contains('is-leaving')) return;
      const key = line.dataset.kcLine;
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
      line.classList.add('is-leaving');
      this.change(key, 0, line);
    }

    async change(key, quantity, line) {
      const before = line ? Number(line.dataset.qty) : NaN;
      if (line && quantity === before) {
        line.classList.remove('is-pending');
        if (!this.querySelector('.is-pending, .is-busy')) this.classList.remove('is-busy');
        return;
      }
      if (line) {
        line.classList.remove('is-pending');
        line.classList.add('is-busy');
        line.setAttribute('aria-busy', 'true');
      }
      this.classList.add('is-busy');
      const title = lineTitle(line);

      try {
        const cart = await post(CHANGE_URL, {
          id: key,
          quantity,
          sections: this.sections().join(','),
          sections_url: here()
        });
        const item = (cart.items || []).find((i) => i.key === key);
        if (quantity > 0 && item && item.quantity < quantity) {
          const text =
            (typeof cart.errors === 'string' && cart.errors) ||
            (strings.quantityError ? strings.quantityError.replace('[quantity]', item.quantity) : ERROR);
          keep.error = { key, text };
        }
        this.render(cart.sections);
        if (quantity === 0) announce(`Removed ${title}`);
        else announce(`${title}: ${item ? item.quantity : quantity}`);
        this.changed(cart);
      } catch (err) {
        if (!line || !line.isConnected) {
          announce(err.message);
          return;
        }
        const input = line.querySelector('[data-kc-qty]');
        if (input && Number.isFinite(before)) input.value = before;
        line.classList.remove('is-busy', 'is-leaving');
        line.removeAttribute('aria-busy');
        this.syncSteps(line);
        this.showError(line, err.cart ? err.message : ERROR);
        if (!this.querySelector('.is-pending, .is-busy')) this.classList.remove('is-busy');
      }
    }

    clearError(line) {
      const box = line.querySelector('[data-kc-line-error]');
      if (box) {
        box.textContent = '';
        box.hidden = true;
      }
    }

    showError(line, text) {
      const box = line.querySelector('[data-kc-line-error]');
      if (!box) return;
      box.textContent = text.replace(/<[^>]*>/g, '');
      box.hidden = false;
    }

    /* --- swapping in the new HTML ---------------------------------------- */

    render(sections) {
      const html = sections && sectionHTML(sections[this.sections()[0]]);
      if (!html) {
        // Shopify sent no section (it can't find it): fall back to a refresh.
        if (this.mode === 'drawer') document.dispatchEvent(new CustomEvent('cart:refresh'));
        else window.location.reload();
        return;
      }

      const active = document.activeElement;
      if (active && this.contains(active)) {
        const line = active.closest('[data-kc-line]');
        keep.focus = {
          key: line ? line.dataset.kcLine : null,
          index: line ? this.lines.indexOf(line) : -1,
          ctl: ctlOf(active)
        };
      }
      const scroller = this.querySelector('[data-kc-scroll]');
      if (scroller) keep.scroll = scroller.scrollTop;
      const bar = this.querySelector('.kc-meter__bar i');
      if (bar) keep.bar = bar.style.width;

      const host =
        this.mode === 'drawer'
          ? document.getElementById('mini-cart')
          : document.getElementById(`shopify-section-${this.dataset.section}`);
      if (host) host.innerHTML = html;

      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleHTML = sectionHTML(sections['cart-icon-bubble']);
      if (bubble && bubbleHTML !== null) bubble.innerHTML = bubbleHTML;
    }

    changed(cart) {
      document.dispatchEvent(new CustomEvent('kc:bag-changed', { detail: { cart } }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kc-bag', cartData: cart });
      }
    }

    /* The new <kc-bag> picks up where the old one left off. */
    restore() {
      const scroller = this.querySelector('[data-kc-scroll]');
      if (scroller && keep.scroll !== null) scroller.scrollTop = keep.scroll;
      keep.scroll = null;

      // The free-shipping bar grows (or shrinks) from where it was.
      const bar = this.querySelector('.kc-meter__bar i');
      if (bar && keep.bar && keep.bar !== bar.style.width) {
        const to = bar.style.width;
        bar.style.width = keep.bar;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          bar.style.width = to;
        }));
      }
      keep.bar = null;

      if (keep.note !== null) {
        const field = this.querySelector('[data-kc-note]');
        const toggle = this.querySelector('[data-kc-note-toggle]');
        if (field) {
          field.value = keep.note;
          const box = field.closest('[hidden]');
          if (box) box.hidden = false;
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
      }

      if (keep.error) {
        const line = this.lineByKey(keep.error.key);
        if (line) this.showError(line, keep.error.text);
        keep.error = null;
      }

      const focus = keep.focus;
      keep.focus = null;
      if (!focus) return;

      let el = null;
      const line = focus.key && this.lineByKey(focus.key);
      if (line && focus.ctl) el = line.querySelector(focus.ctl);
      if (!el && focus.key) {
        // The line went away: its neighbour's remove button, else the head.
        const lines = this.lines;
        const near = lines[Math.min(Math.max(focus.index, 0), lines.length - 1)];
        if (near) el = near.querySelector('[data-kc-remove]');
      }
      if (!el && focus.ctl) el = this.querySelector(focus.ctl);
      if (!el) el = this.querySelector('[data-kc-close]') || this.querySelector('.kc-bag__title');
      if (!el) return;
      if (!el.matches('a[href], button, input, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');

      const details = this.mode === 'drawer' && this.closest('details[open]');
      if (details && typeof window.trapFocus === 'function') window.trapFocus(details, el);
      else el.focus({ preventScroll: true });
    }

    /* --- note ------------------------------------------------------------- */

    toggleNote(button) {
      const box = document.getElementById(button.getAttribute('aria-controls'));
      if (!box) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      box.hidden = !open;
      if (open) {
        const field = box.querySelector('textarea');
        if (field) field.focus();
      } else {
        saveNote();
      }
    }

    /* --- suggestions in the drawer ---------------------------------------- */

    fillDrawerRecs() {
      const box = this.querySelector('[data-kc-recs]');
      if (!box) return;
      const url = box.dataset.url;
      const show = (html) => {
        if (!box.isConnected) return;
        const ids = new Set((this.dataset.cartIds || '').split(','));
        const rows = Array.from(parse(html).querySelectorAll('[data-kc-recs-picks] > li'))
          .filter((li) => !ids.has(li.dataset.productId))
          .slice(0, Number(box.dataset.count) || 3);
        box.querySelector('[data-kc-recs-list]').replaceChildren(...rows.map((li) => document.importNode(li, true)));
        box.hidden = rows.length === 0;
      };
      if (keep.recs.has(url)) {
        show(keep.recs.get(url));
        return;
      }
      fetch(url)
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => {
          keep.recs.set(url, html);
          show(html);
        })
        .catch(() => {});
    }

    /* --- adding on the cart page ------------------------------------------ */

    async add(button) {
      if (button.classList.contains('loading')) return;
      button.classList.add('loading');
      try {
        const item = await post(ADD_URL, {
          id: Number(button.dataset.variantId),
          quantity: 1,
          sections: this.sections().join(','),
          sections_url: here()
        });
        this.render(item.sections);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item } }));
        announce(`Added ${item.product_title || ''}`.trim());
        this.changed(item);
      } catch (err) {
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:error', { detail: { errorMessage: err.cart ? err.message : ERROR } })
        );
      } finally {
        button.classList.remove('loading');
      }
    }
  }

  customElements.define('kc-bag', KcBag);

  /* The theme's <add-to-cart> posts the whole page to /cart/add on the cart
     page (and loses ?view=). Catch it first and add in place instead. */
  document.addEventListener(
    'click',
    (event) => {
      const add = event.target.closest && event.target.closest('add-to-cart[data-variant-id]');
      if (!add) return;
      const bag = document.querySelector('kc-bag[data-mode="page"]');
      if (!bag) return;
      event.preventDefault();
      event.stopPropagation();
      bag.add(add);
    },
    true
  );

  window.addEventListener('pagehide', saveNote);

  class KcBagRecs extends HTMLElement {
    connectedCallback() {
      this.onBag = () => this.load();
      document.addEventListener('kc:bag-changed', this.onBag);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('kc:bag-changed', this.onBag);
    }

    async load() {
      const bag = document.querySelector('kc-bag[data-mode="page"]');
      const first = bag && bag.dataset.first;
      if (!first) {
        this.hidden = true;
        return;
      }
      const url = `${this.dataset.base}&product_id=${encodeURIComponent(first)}`;
      const token = (this.token = (this.token || 0) + 1);
      try {
        let html = keep.recs.get(url);
        if (!html) {
          const res = await fetch(url);
          if (!res.ok) throw new Error(String(res.status));
          html = await res.text();
          keep.recs.set(url, html);
        }
        if (token !== this.token) return;
        const ids = new Set((bag.dataset.cartIds || '').split(','));
        const cards = Array.from(parse(html).querySelectorAll('[data-kc-recs-cards] > li'))
          .filter((li) => !ids.has(li.dataset.productId))
          .slice(0, Number(this.dataset.count) || 4);
        const list = this.querySelector('[data-kc-recs-list]');
        list.replaceChildren(...cards.map((li) => document.importNode(li, true)));
        // Tablets show three across: drop an uneven last row (kawaii-kit.css).
        const n = cards.length;
        list.className = n > 3 && n % 3 ? `kc-grid kc-grid--tablet-${Math.floor(n / 3) * 3}` : 'kc-grid';
        this.hidden = n === 0;
      } catch (e) {
        this.hidden = true;
      }
    }
  }

  customElements.define('kc-bag-recs', KcBagRecs);
})();
